import { Loader2Icon } from "lucide-react";

import { CmmdProjectImportGroups } from "./CmmdProjectImportGroups";
import type { ComponentProps } from "react";

/**
 * Says the CMMD project list is refreshing without taking it away.
 *
 * The refresh behind that list costs 10-18s, so the cached rows are shown
 * rather than hidden behind a spinner. They stay selectable: a project deleted
 * upstream is already stripped from the cache by its tombstone, and selecting
 * one round trips to the server, which fails loudly on a dead project. This
 * keeps the possible staleness visible rather than implied.
 */
function CmmdProjectSyncingNotice() {
  return (
    <div
      className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground/80"
      role="status"
    >
      <Loader2Icon aria-hidden="true" className="size-3 motion-safe:animate-spin" />
      Syncing CMMD projects...
    </div>
  );
}

type ImportGroupsProps = ComponentProps<typeof CmmdProjectImportGroups>;

export interface CmmdProjectImportRowsProps {
  readonly isRefreshing: boolean;
  readonly disabled: ImportGroupsProps["disabled"];
  readonly groups: ImportGroupsProps["groups"];
  readonly searchActive: ImportGroupsProps["searchActive"];
  readonly onSelectProject: ImportGroupsProps["onSelectProject"];
}

/** The CMMD project rows, with the refreshing notice above them when one is running. */
export function CmmdProjectImportRows(props: CmmdProjectImportRowsProps) {
  return (
    <>
      {props.isRefreshing ? <CmmdProjectSyncingNotice /> : null}
      <CmmdProjectImportGroups
        variant="import"
        idPrefix="import"
        disabled={props.disabled}
        groups={props.groups}
        searchActive={props.searchActive}
        onSelectProject={props.onSelectProject}
      />
    </>
  );
}
